import { useEffect, useState } from 'react'
import _ from 'lodash'
import { useTreeState, findTargetPathByProp, findTargetNode } from '@hooks/useTreeState'
import { FilterFolderConfig } from '../types'

export const createElementIdFromPath = (path: string) => path.replaceAll('/', '')
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

interface useLazyFolderFilterArgs {
  selectedIntegrationAccountId: string
  filterKey: string
  filter: string | string[]
  folderConfig?: FilterFolderConfig
  onIntegrationChange: (value: string, key: string) => void
}

export const useLazyFolderFilter = ({
  selectedIntegrationAccountId,
  folderConfig,
  filterKey,
  filter,
  onIntegrationChange,
}: useLazyFolderFilterArgs) => {
  const [fetchedFolder, setFetchedFolder] = useState<any>(null)

  const [_selectedIntegrationAccountId, _setSelectedIntegrationAccountId] = useState(
    selectedIntegrationAccountId,
  )

  const fetchChildrenOnToggleOpen = (newState, event) => {
    const { type, params } = event

    if (!folderConfig || !params?.length) {
      return
    }

    if (type === 'setIsOpen' && params[1]) {
      const folderDirectoryPath = params[0]
      const targetNodePath = findTargetPathByProp(newState, 'path', folderDirectoryPath)

      if (!targetNodePath) {
        return
      }

      const changedFolder = findTargetNode(newState, targetNodePath)

      if (changedFolder && _.get(changedFolder, 'children.length', 0) < 1) {
        void folderConfig.folderFetcher(changedFolder.path).then(children => {
          setFetchedFolder({ path: changedFolder.path, children })
        })
      }
    }
  }

  const setIsOpen = (root, _, folderDirectoryPath, value) => {
    const targetNodePath = findTargetPathByProp(root, 'path', folderDirectoryPath)

    if (targetNodePath) {
      const targetNode = findTargetNode(root, targetNodePath)
      targetNode.open = value
    }

    return { ...root }
  }

  const setNodeChildren = (root, _, folderDirectoryPath, value) => {
    const targetNodePath = findTargetPathByProp(root, 'path', folderDirectoryPath)
    if (targetNodePath) {
      const targetNode = findTargetNode(root, targetNodePath)
      targetNode.children = value
      if (!targetNode.open) targetNode.open = true
    }
    return { ...root }
  }

  const initialState = {
    _id: -1,
    label: 'Root Directory',
    path: '/',
    open: false,
  }

  const { treeState, reducers } = useTreeState({
    data: initialState,
    onChange: fetchChildrenOnToggleOpen,
    customReducers: {
      setIsOpen,
      setNodeChildren,
    },
  })

  const updateTreeStateAfterFolderFetch = (fetchedFolder: any) => {
    const children = fetchedFolder?.children ?? []
    reducers.setNodeChildren([], fetchedFolder.path, children)
  }

  useEffect(() => {
    if (fetchedFolder) updateTreeStateAfterFolderFetch(fetchedFolder)
  }, [fetchedFolder])

  const fetchFoldersOnLoad = async (filter: string | string[]) => {
    if (!fetchedFolder && filter && filter.toString() !== '/' && folderConfig) {
      const folders = filter
        .toString()
        .split('/')
        .filter(el => el !== '')
        .map((_, index, array) => `/${array.slice(0, index).join('/')}`)
      for (const folder of folders) {
        const children = await folderConfig.folderFetcher(folder)
        setFetchedFolder({ path: folder, children })
        await wait(300)
      }
    }
  }

  useEffect(() => {
    void fetchFoldersOnLoad(filter)
  }, [filter])

  const resetFilter = () => {
    reducers.setTreeState(initialState)
    onIntegrationChange('/', filterKey)
  }

  useEffect(() => {
    if (_selectedIntegrationAccountId !== selectedIntegrationAccountId) {
      resetFilter()
      _setSelectedIntegrationAccountId(selectedIntegrationAccountId)
    }
  }, [selectedIntegrationAccountId])

  return { treeState, reducers }
}
